import Config from 'es6/core/config';
import Transliterable from 'es6/models/transliterable';

const DISTRACTOR_COUNT = 3;

const DISTRACTOR_BLACKLIST = Object.freeze({
  ja: ['よ', 'ね', 'の', 'だ', 'です', 'が'],
});

const HTML_TAG_PATTERN = /<\/?[^>]+(>|$)/g;

const PUNCTUATION_PATTERN = /[\s~`!\/\/@#$%^&*_+=(){}\[\]<>|:;",.?\\「」『』〈〉【】《》～〜。、・．／…？！＠＃＄％＾＆＊（）＝＋０１２３４５６７８９0-9]/g;

export default class Sentence {
  constructor(attrs = {}) {
    _.defaults(this, attrs, {
      goalId: null,

      itemId: null,

      position: null,

      distractors: [],

      cue: {
        id: null,
        text: null,
        transliterations: {},
        language: null,
      },

      response: {
        text: null,
        language: null,
        transliterations: {},
      },
    });

    this.cue = new Transliterable(this.cue);
    this.response = new Transliterable(this.response);
  }

  getId() {
    return this.cue.id;
  }

  getImage() {
    return this.image;
  }

  getSound() {
    return this.sound;
  }

  /**
   * Retrieves word entities for a sentence.
   *
   * @returns {Object[]}   objects containing value field and isPunctuation
   */
  constructWordEntities() {
    this._wordEntities = [];
    const text = this.getBuilderText(true);

    if (text.length === 0) {
      return this._wordEntities;
    }

    // Remove markup that is included in content.
    const textWithoutHTML = text.replace(HTML_TAG_PATTERN, '');
    const words           = textWithoutHTML.split(/\s/g);

    let isPreviousLetterPunctuation = false;
    let isCurrentLetterPunctuation;

    for (const word of words) {
      let result = '';

      for (const letter of word) {
        isCurrentLetterPunctuation = letter.match(PUNCTUATION_PATTERN) !== null;

        // Group clusters of punctuation together.
        if (isPreviousLetterPunctuation !== isCurrentLetterPunctuation) {
          if (result.length > 0) {
            this._wordEntities.push({ value: result, isPunctuation: isPreviousLetterPunctuation });
          }

          result = '';
          isPreviousLetterPunctuation = isCurrentLetterPunctuation;
        }

        result += letter;
      }

      if (result.length > 0) {
        this._wordEntities.push({ value: result, isPunctuation: isCurrentLetterPunctuation });
      }
    }

    return this._wordEntities;
  }

  get wordEntities() {
    if (!this._wordEntities) {
      this.constructWordEntities();
    }

    return this._wordEntities;
  }

  /**
   * Given a set of distractors, return appropriate distractors for
   * the sentence.
   *
   * @param {String[]} distractorPool     list of possible distractors
   * @returns {String[]}
   */
  getWordDistractors(distractorPool = []) {
    const wordEntities = this.wordEntities
      .filter(w => !w.isPunctuation)
      .map(w => w.value);

    return _.chain(distractorPool)
      .difference(wordEntities)
      .uniq()
      .shuffle()
      .filter(d => Sentence.isValidDistractor(d, this.getLanguage()))
      .value()
      .slice(0, DISTRACTOR_COUNT);
  }

  /**
   * Gets the language.
   *
   * @returns {String}
   */
  getLanguage() {
    return this.cue.language;
  }

  /**
   * Custom functionality to return the appropriate transliteration
   * for builder quizzes.
   *
   * @returns {String}
   */
  getBuilderText() {
    const lang = this.cue.getLanguageShortCode();
    const transliterations = this.cue.transliterations;

    // If the user is not in romaji or kana mode, return the special
    // Jpn transliteration which inserts spaces in the full text.
    if (lang === 'ja' && !Config.langSettings.romajiMode && !Config.langSettings.kanaMode) {
      return transliterations.Jpan;
    }

    if (lang === 'zh') {
      return transliterations.Latn;
    }

    return this.cue.getText();
  }

  /**
   * Custom functionality to return the appropriate text for fill
   * in the blank quizzes. Japanese should return the kana transliteration
   * for normal mode or kana mode.
   *
   * Chinese should use transliteration if available.
   *
   * @returns {String}
   */
  getFillInBlankText() {
    if (this.cue.language === 'ja' && !Config.langSettings.romajiMode) {
      return this.cue.transliterations.Hrkt;
    }

    return this.cue.getTransliteration() || this.cue.getText();
  }

  /**
   * Returns the appropriate text from the cue
   *
   * @returns {String}
   */
  getText() {
    return this.cue.getText();
  }

  /**
   * Returns the appropriate transliteration from the cue.
   *
   * @returns {String}
   */
  getTransliteration() {
    return this.cue.getTransliteration();
  }

  getTranslation() {
    return this.response && this.response.text || null;
  }

  /**
   * Determines if the transliterable is generally okay to be
   * used for sentences.
   *
   * Things that can make a distractor invalid:
   * 1) If it only contains punctuation.
   * 2) Is not capitalized.
   * 3) If the word is blacklisted.
   *
   * @param {String} text
   * @param {String} language
   * @returns {Boolean}
   */
  static isValidDistractor(text, language) {
    const languageBlacklist = DISTRACTOR_BLACKLIST[language] || [];
    const sanitizedText = text.replace(PUNCTUATION_PATTERN, '');
    const firstCharacter = sanitizedText.substr(0, 1);

    return sanitizedText.length !== 0 &&
      firstCharacter.toLowerCase() === firstCharacter &&
      !languageBlacklist.find(blacklisted => blacklisted === sanitizedText);
  }
}
